import { useEffect, useRef, useState, type InputHTMLAttributes, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn';

import { Icon } from './Icon';

export interface SearchInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'placeholder' | 'className'
> {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  /** What can be searched for; "Search" when not given. */
  placeholder?: string;
  /** Guided-tour anchor, rendered as `data-tour` on the root (an `AnchorId`; CONTRACT §3). */
  tourId?: string;
  /** How long typing must pause before `onChange` runs. 300 ms by default. */
  debounceMs?: number;
  /** Classes for the root (its width); the input itself keeps the R-050 field. */
  className?: string;
  /**
   * React 19 passes `ref` to a function component as an ordinary prop. Here it
   * reaches the INPUT, so a caller can focus it — a dialog that opens on it,
   * say.
   */
  ref?: Ref<HTMLInputElement>;
}

/**
 * Debounced search box.
 *
 * The server decides what matches (a case-insensitive substring match on the
 * fields each list allows), so nothing here transliterates or guesses. The
 * text typed is sent once typing pauses; the clear control empties it at once.
 *
 * R-050 SearchInput: the input with its leading search glyph on the
 * inline-start side in either direction, and — once there is something to
 * clear — a 44px clear control at the inline end. The browser's own clear
 * glyph is hidden (`fd-search-input`), so there is one, and it is the same in
 * every browser.
 *
 * Every other input attribute (a combobox role, `aria-activedescendant`, a key
 * handler) passes through to the input.
 */
export function SearchInput({
  value,
  onChange,
  label,
  placeholder,
  tourId,
  debounceMs = 300,
  className,
  ref,
  ...inputProps
}: SearchInputProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const input = useRef<HTMLInputElement | null>(null);
  // What this field has handed its parent and not yet seen come back as
  // `value`, oldest first. That echo is the text as it was when the pause
  // elapsed; a key typed before the parent's render (and its effects) caught
  // up is newer, so the echo is never written over the field: what the user
  // types stays typed. Any other value — a reset, a recent term — is the
  // parent's own, and the field shows it.
  const handed = useRef<string[]>([]);

  useEffect(() => {
    const echo = handed.current.indexOf(value);
    handed.current = echo < 0 ? [] : handed.current.slice(echo + 1);

    if (echo < 0) setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return;

    const timer = setTimeout(() => {
      handed.current.push(draft);
      onChange(draft);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [draft, value, onChange, debounceMs]);

  const attach = (element: HTMLInputElement | null) => {
    input.current = element;

    if (typeof ref === 'function') ref(element);
    else if (ref) ref.current = element;
  };

  const clear = () => {
    setDraft('');

    if (value !== '') {
      handed.current.push('');
      onChange('');
    }

    input.current?.focus();
  };

  return (
    <span data-tour={tourId} className={cn('relative block w-full sm:max-w-xs', className)}>
      <Icon
        name="search"
        size={16}
        className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-ink-muted"
      />
      <input
        {...inputProps}
        ref={attach}
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder ?? t('app.search')}
        aria-label={inputProps['aria-label'] ?? label ?? t('app.search')}
        className={cn(
          'fd-search-input min-h-12 w-full rounded-lg border-0 bg-card ps-9 text-sm text-ink ring-1 ring-line sm:min-h-11',
          draft === '' ? 'pe-3' : 'pe-11',
        )}
      />
      {draft !== '' && !inputProps.disabled && !inputProps.readOnly ? (
        <button
          type="button"
          onClick={clear}
          aria-label={t('search.clearInput')}
          className="absolute end-0 top-1/2 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-lg text-ink-muted hover:text-ink focus-ring-inset focus-visible:outline-2 focus-visible:outline-focus"
        >
          <Icon name="close" size={16} />
        </button>
      ) : null}
    </span>
  );
}
